import { cn } from '@/utils/cn'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { type VariantProps, cva } from 'class-variance-authority'
import { type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes, forwardRef } from 'react'
import { CloseIcon } from '../icons/close'
import { Button } from './button'
import { ScrollArea } from './scrollArea'

const dialogVariants = cva(
  cn(
    'fixed z-50 flex max-h-screen w-full max-w-xl flex-col overflow-hidden bg-surface',
    'data-[state=closed]:animate-out data-[state=open]:animate-in'
  ),
  {
    variants: {
      position: {
        center: cn(
          'top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] rounded-2xl',
          'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
        ),
        right: cn(
          'top-0 right-0 h-full rounded-l-2xl',
          'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
        ),
        bottom: cn(
          'bottom-0 left-0 w-full rounded-t-2xl',
          'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom'
        ),
      },
      fadable: {
        true: 'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        false: '',
      },
    },
    defaultVariants: {
      position: 'center',
      fadable: true,
    },
  }
)

const dialogOverlayVariants = cva(
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 data-[state=closed]:animate-out data-[state=open]:animate-in',
  {
    variants: {
      overlay: {
        default: 'bg-neutral-800/30 backdrop-blur-[6px]',
        hidden: 'bg-transparent backdrop-blur-none',
      },
    },
    defaultVariants: {
      overlay: 'default',
    },
  }
)

export const Dialog = DialogPrimitive.Root

export const DialogTrigger = DialogPrimitive.Trigger

export const DialogClose = DialogPrimitive.Close

const DialogPortal = DialogPrimitive.Portal

const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ ...props }, ref) => <DialogPrimitive.Overlay ref={ref} {...props} />)
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogCrossClose = forwardRef<ElementRef<typeof Button>, ComponentPropsWithoutRef<typeof Button>>(
  ({ className, ...props }, ref) => (
    <Button
      ref={ref}
      size="default"
      variant="default"
      className={cn('absolute rounded-full', className)}
      asChild
      {...props}
    >
      <DialogClose>
        <CloseIcon size={24} />
        <span className="sr-only">Close</span>
      </DialogClose>
    </Button>
  )
)
DialogCrossClose.displayName = 'DialogCrossClose'

type DialogContentProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Content> &
  VariantProps<typeof dialogVariants> &
  VariantProps<typeof dialogOverlayVariants> & {
    hideCloseButton?: boolean
  }
export const DialogContent = forwardRef<ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, children, position, fadable, overlay, hideCloseButton, ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay className={cn(dialogOverlayVariants({ overlay }))} />
      <DialogPrimitive.Content ref={ref} className={cn(dialogVariants({ position, fadable, className }))} {...props}>
        {children}
        {!hideCloseButton && <DialogCrossClose className="top-4 right-4 md:top-8 md:right-8" />}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
)
DialogContent.displayName = DialogPrimitive.Content.displayName

export const DialogScrollArea = ({ className, ...props }: ComponentPropsWithoutRef<typeof ScrollArea>) => (
  <ScrollArea
    type="always"
    className={cn('min-h-0 flex-1 overflow-y-auto', '[&_[data-role=scroll-bar-thumb]]:bg-border', className)}
    {...props}
  />
)
DialogScrollArea.displayName = 'DialogScrollArea'

export const DialogContentWrapper = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-4 p-5 md:p-8', className)} {...props} />
)
DialogContentWrapper.displayName = 'DialogContentWrapper'

export const DialogHeaderWrapper = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-4 px-6 pt-10 pb-0 md:px-8 md:pt-16', className)} {...props} />
)
DialogHeaderWrapper.displayName = 'DialogHeaderWrapper'

export const DialogHeader = ({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <header className={cn(className)} {...props}>
    {children}
  </header>
)
DialogHeader.displayName = 'DialogHeader'

export const DialogBody = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex h-full flex-col overflow-hidden', className)} {...props} />
)
DialogBody.displayName = 'DialogBody'

export const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center border-t bg-surface p-4', className)} {...props} />
)
DialogFooter.displayName = 'DialogFooter'

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('font-medium text-2xl text-heading-foreground', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm', className)} {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName
